import { useGetClient } from '@biasafe.queries/clients-client-id/clients-client-id';
import { FactGrid } from './FactGrid';
import type { Fact } from './FactGrid';
import { Skeleton } from '@/components/ui/skeleton';

const fmtMoney = (v: number | null | undefined) =>
  v == null
    ? null
    : new Intl.NumberFormat('en-US', {
        // No currency: a client's AUM is recorded without one.
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(v);

const fmtPct = (v: number | null | undefined) =>
  v == null ? null : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;

const fmtDate = (v: string | null | undefined) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/** "in_review" -> "In review". The API returns raw enum slugs. */
const humanise = (v: string | null | undefined) => {
  if (!v) return null;
  const words = v.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export interface ClientFactsProps {
  clientId: string;
  /**
   * Shown when the client cannot be read.
   *
   * Not `null`. Falling back to nothing made a client notification with an
   * unreachable record show LESS than an ordinary one — the pane lost its
   * whole facts block and went back to being the sheet of white this feature
   * exists to fix. The generic facts are always available, so a failed fetch
   * costs the extra detail and nothing else.
   */
  fallback?: React.ReactNode;
}

/**
 * The client behind a `client:` notification, fetched and laid out.
 *
 * WHY A FETCH AT ALL, when nothing else in this feature makes one. The
 * notification payload for a new client is the word "ready" and a display
 * name — `_clients` in `notifications/service.py` sends `subtitle = name` and
 * nothing else. Rendered as prose that is two words, and the reader pane is
 * three quarters of a 70vw dialog: the panel opened on a real client and
 * showed a screen-wide sheet of white with "jiji coco" in the corner.
 *
 * The id is the fix. `client:<uuid>` carries `Client.id` — the same id
 * `/clients/$clientId` and `getClient` take (checked against `_clients`, which
 * selects `Client.id` directly) — so the pane can ask for the row the
 * notification is about and show it. That is what an inbox reader is: the
 * message names a thing, the reader shows the thing.
 *
 * The two OTHER kinds that carry an exactly-resolving id do not get this
 * treatment yet, for one reason each: `strat:` carries `Strategy.id`, the
 * VERSION row's primary key, where `getStrategy` wants `strategy_id` (see
 * `destination.ts`); and `user:` has no per-member endpoint. Neither is a
 * judgement call about value — both are a missing id.
 *
 * FAILURE IS SILENT ON PURPOSE. A client the caller cannot read, or one
 * deleted since the feed was built, drops to the caller's `fallback` without a
 * word. An error banner here would be about a request the user never made, over
 * a notification that is itself still perfectly true.
 */
export const ClientFacts = ({ clientId, fallback }: ClientFactsProps) => {
  const { data: client, isPending, isError } = useGetClient(clientId);

  if (isPending) {
    return (
      <div className="grid grid-cols-2 gap-x-8 gap-y-4" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) return <>{fallback ?? null}</>;

  const facts: Array<Fact> = [
    { label: 'Reference', value: client.referenceCode },
    { label: 'Type', value: humanise(client.clientType) },
    { label: 'Status', value: humanise(client.status), emphasis: true },
    { label: 'Risk level', value: humanise(client.riskLevel) },
    {
      label: 'Assets under management',
      value: fmtMoney(client.assetsUnderManagement),
    },
    {
      label: 'Return, year to date',
      value: fmtPct(client.ytdReturnPct),
      // Losses are worth seeing at a glance in a list of otherwise flat facts.
      tone:
        client.ytdReturnPct == null
          ? undefined
          : client.ytdReturnPct < 0
            ? 'negative'
            : 'positive',
    },
    { label: 'Advisor', value: client.assignedAdvisorName },
    { label: 'Onboarding', value: humanise(client.onboardingStatus) },
    {
      label: 'Brokerage account',
      value:
        client.hasBrokerageAccount === undefined
          ? null
          : client.hasBrokerageAccount
            ? 'Connected'
            : 'None yet',
    },
    { label: 'KYC review due', value: fmtDate(client.kycReviewDueAt) },
    { label: 'Email', value: client.email },
    { label: 'Phone', value: client.phone },
  ];

  return <FactGrid facts={facts} />;
};
